import { AutocompleteWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import { Chip, IconButton, Typography } from '@material-ui/core'
import Cancel from '@material-ui/icons/Cancel'
import Alert from '@material-ui/lab/Alert'
import { toast } from 'react-toastify'

import {
  ADD_TICKET_WATCHER_MUTATION,
  REMOVE_TICKET_WATCHER_MUTATION
} from '../../graphql/tickets'
import { USERS_LIST_QUERY } from '../../graphql/userManagement'
import { Ticket } from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import { UserBrief, UserList } from '../../types/users'
import SectionCard from './SectionCard'
import { useEligibleWatchers } from './useEligibleWatchers'

interface Props {
  ticket: Ticket
}

/**
 * Shape fixed by the design-system Autocomplete, which is typed against
 * `{ label, value }`. `value` carries the user id.
 */
interface UserOption {
  label: string
  value: string
}

/**
 * Watcher management.
 *
 * There is no "watch this ticket myself" affordance, and that is a constraint
 * rather than an omission: `AddWatcher` rejects an empty user id outright, and
 * the client's stored credentials carry name/email/entity but no user id. So
 * the caller cannot name itself, and a picker is the only shape the API
 * actually supports.
 */
// The picker holds no selection of its own — `value` is always null.
// The chip list is the single account of who is watching, and a picker that
// also remembered picks could drift out of step with it: the component is
// single-select, so a retained selection would accumulate
// entries that were never successfully added. The control is single-select, so
// each pick is one add and the input clears itself.
/**
 * A machine identity, which can never be a watcher.
 *
 * Recognised from its ROLE rather than its email, because the email shapes vary
 * (`ticket-service@service.invalid` but also `pmsmanager.service@adotel.net`)
 * while IAM's seeded service roles are a closed, named set — `Service_*`, the
 * same prefix `isMachineRole` keys on in the seeder. A heuristic on the address
 * would let the next service account through under a new domain.
 */
function isServiceIdentity(user: UserBrief): boolean {
  return (user.role || '').startsWith('Service_')
}

/**
 * Turns a watcher-add refusal into something a support agent can act on.
 *
 * ticket-service refuses a target who cannot already see the ticket, and it is
 * right to: AddWatcher would otherwise be a backdoor that grants read access to
 * anyone, and ticket bodies carry customer PII (ticket_server.go, AddWatcher —
 * "never use watcher-add as a backdoor"). The refusal is correct; what reached
 * the operator was not.
 *
 * What they saw was the wire error verbatim —
 *   `rpc error: code = FailedPrecondition desc = target user does not have
 *    visibility on this ticket and cannot be added as a watcher`
 * — which names a gRPC status code and a service-internal invariant to someone
 * whose next action is choosing a different colleague. It reads as a fault in
 * the feature rather than a fact about that one person, which is how the same
 * screen got reported broken three times.
 *
 * The name is interpolated because the picker clears on failure: without it the
 * message describes a person the operator can no longer see.
 *
 * Anything unrecognised passes through untouched. A translation table that
 * swallowed unknown errors would turn a real outage into a confident, wrong
 * explanation — worse than the raw string this replaces.
 */
export function watcherErrorMessage(raw: string, who: string): string {
  if (raw.includes('does not have visibility on this ticket')) {
    // One single-quoted literal, deliberately. The localization coverage test
    // detects translatable strings with /getTranslate\('([^'\\]|\\.)+'/ — a
    // concatenation or a double-quoted string is invisible to it, so the key
    // would silently never be flagged as needing Persian.
    return getTranslate(
      '{name} cannot be added as a watcher, because they do not have access to the organization this ticket belongs to',
      { name: who }
    )
  }
  return raw
}

export default function TicketWatchers({ ticket }: Props) {
  // usersError is read, not discarded. When the directory query fails the list
  // is empty and every existing watcher falls back to a raw hex id — so the
  // panel reads as "there are no users and the watchers are gibberish" rather
  // than "the directory did not load". Two very different things to be told,
  // and the silent one sends you looking in the wrong place.
  const {
    data: usersData,
    loading: loadingUsers,
    error: usersError
  } = useQuery<{
    users: UserList
  }>(USERS_LIST_QUERY, { notifyOnNetworkStatusChange: true })

  // Both mutations return the full ticket, so the mounted TICKET_QUERY updates
  // through cache normalisation on `Ticket:<id>` — no refetch, same reasoning
  // as transition/assign in the parent screen.
  const [addWatcher, addState] = useMutation<{ addTicketWatcher: Ticket }>(
    ADD_TICKET_WATCHER_MUTATION
  )
  const [removeWatcher, removeState] = useMutation<{
    removeTicketWatcher: Ticket
  }>(REMOVE_TICKET_WATCHER_MUTATION)

  const busy = addState.loading || removeState.loading

  const allUsers = usersData?.users?.users ?? []

  // Watchers are stored as bare ids. Rendering them raw shows a column of
  // opaque identifiers, so they are resolved against the same directory that
  // feeds the picker — and an id with no match still renders as itself rather
  // than disappearing, which would silently under-report the audience.
  const watcherLabel = (userId: string): string => {
    const match = allUsers.find((user) => user.ID === userId)
    if (!match) return userId
    return match.name || match.email || userId
  }

  // Already-watching users are filtered out rather than shown as selected:
  // the chip list above is the state, the picker is only the add affordance.
  //
  // Service identities and deactivated accounts are filtered too, and the
  // reason is stronger than tidiness: ticket-service refuses any target without
  // standing visibility on the ticket, so these entries could NEVER be added.
  // The list was offering `ticket-service@service.invalid` and
  // `notification-service@service.invalid` alongside real people, and every
  // pick failed identically — which reads as "adding a watcher is broken"
  // rather than "that one cannot be a watcher".
  //
  // This does NOT replicate the server's rule, and must not pretend to:
  // visibility depends on the ticket's reporter entity and affected refs,
  // which this component does not resolve. The server stays the authority and
  // its refusal still surfaces through `toast.error` below. What this removes
  // is the subset that is knowably impossible before the round trip.
  const candidates: UserOption[] = allUsers
    .filter((user) => !ticket.watcherUserIds.includes(user.ID))
    // `!== false`, not a truthy check. A directory entry that simply omits
    // the field must stay visible: hiding everyone because a payload changed
    // shape would empty the picker silently, which reads as "watchers are
    // broken" rather than "this response is missing a field". Only an explicit
    // deactivation removes someone.
    .filter((user) => user.active !== false)
    .filter((user) => !isServiceIdentity(user))
    .map((user) => ({
      value: user.ID,
      label: user.name ? `${user.name} (${user.email})` : user.email
    }))

  // Ask the server which of them may actually be added.
  //
  // This is the whole change. The list above is the user DIRECTORY minus three
  // things the panel can know locally — already watching, deactivated, a
  // service identity. It is not a candidate list, and treating it as one is why
  // an Adotel-internal ticket offered an external provider with no grants at
  // all: the server refused, correctly, after the operator had already picked
  // him.
  //
  // Eligibility cannot be computed here. It is "admin, or a named participant,
  // or a live grant on the ticket's entity", and a grant lives in the user's
  // IAM resources array, which the directory does not return. Filtering on the
  // single entityCode it DOES return would hide anyone homed elsewhere holding
  // a grant, and is_admin is hardcoded false in every list response, so it
  // would hide every admin — who on an Adotel-internal ticket are the only
  // eligible candidates. So the server answers, using the same predicate
  // addTicketWatcher enforces.
  const {
    eligible,
    loading: loadingEligible,
    error: eligibleError
  } = useEligibleWatchers(
    ticket.id,
    candidates.map((c) => c.value)
  )

  // Empty WHILE LOADING, rather than showing candidates that may be refused.
  // Offering someone and retracting them a moment later is worse than a brief
  // spinner, and the control already shows one.
  const options: UserOption[] = loadingEligible
    ? []
    : candidates.filter((c) => eligible.has(c.value))

  function add(user: UserOption) {
    // Guarded here, not only by `disabled` on the picker: `busy` flips on the
    // NEXT render, so two picks in the same tick both reach this function while
    // the control still looks enabled — and the second one is not filtered out
    // of `options` either, since that list only refreshes once the first
    // mutation resolves. Both would then add the same watcher.
    //
    // Both halves are pinned now. The `includes` half was previously flagged
    // here as untestable, on the grounds that the stub picker selects from an
    // already-filtered option list — true of the rendered control, but the
    // guard defends a STALE one, and a stale option can be replayed through the
    // captured handler. Deleting this clause now fails
    // "ignores a stale pick of someone already watching"; before that test it
    // left the suite green while the server absorbed the duplicate silently
    // (the insert is ON CONFLICT DO NOTHING).
    if (busy || ticket.watcherUserIds.includes(user.value)) return
    addWatcher({
      variables: { ticketId: ticket.id, userId: user.value }
    }).catch((e) => toast.error(watcherErrorMessage(e.message, user.label)))
  }

  function remove(userId: string) {
    removeWatcher({ variables: { ticketId: ticket.id, userId } }).catch((e) =>
      toast.error(e.message)
    )
  }

  return (
    <SectionCard heading={getTranslate('Watching')} level="h3" quiet>
      {/* Rendered ABOVE the chips, not below the picker, because it explains
          both halves of what the reader is looking at: the picker offering
          nobody AND the existing watchers showing as raw ids. Placed after the
          heading so the panel still says what it is before it says what went
          wrong. */}
      {usersError && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {getTranslate(
            'The user directory did not load. Existing watchers are shown by id, and no one can be added until it does.'
          )}
        </Alert>
      )}

      {/* A separate message from the directory failure above, because they are
          different faults with different remedies: the directory is missing
          versus the eligibility check is. Collapsing them would send a reader
          looking in the wrong place — the same defect the directory message
          was written to avoid.

          The picker is empty in this state by design. Falling back to the
          unfiltered directory would restore exactly the behaviour this change
          removes — offering people the server will refuse — silently, and only
          when something is already broken. */}
      {!usersError && eligibleError && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {getTranslate(
            'Could not check who may be added to this ticket, so the list is empty. Existing watchers are unaffected.'
          )}
        </Alert>
      )}

      <div style={{ marginTop: 8 }}>
        {ticket.watcherUserIds.length ? (
          ticket.watcherUserIds.map((userId) => (
            <Chip
              key={userId}
              size="small"
              label={watcherLabel(userId)}
              onDelete={busy ? undefined : () => remove(userId)}
              // MUI's default delete affordance is a bare <svg> that SvgIcon
              // marks `aria-hidden` and `focusable="false"` — so it is absent
              // from the accessibility tree, and role/aria-label passed to the
              // icon are ignored. A real IconButton is the fix; naming it per
              // watcher also disambiguates when several chips are on screen.
              deleteIcon={
                <IconButton
                  size="small"
                  aria-label={`${getTranslate('Remove watcher')} ${watcherLabel(
                    userId
                  )}`}>
                  <Cancel fontSize="small" />
                </IconButton>
              }
              style={{ marginInlineEnd: 8, marginBottom: 8 }}
            />
          ))
        ) : (
          <Typography variant="body2" color="textSecondary">
            {getTranslate('nobody')}
          </Typography>
        )}
      </div>

      <div style={{ marginTop: 8, maxWidth: 420 }}>
        <AutocompleteWithLabel
          /* SINGLE-select, deliberately, and this reverses an earlier fix.
             
             The handler used to call `values.forEach(add)` while the component
             defaulted to single-select, so it received one option object and
             threw `TypeError: values.forEach is not a function` — picking a name
             did nothing at all. Adding `multiple` stopped the crash and created
             a worse screen: the component does `disableCloseOnSelect: multiple`,
             so the dropdown never closed, picked names piled up as chips inside
             the input, and there was no confirm step to end on. Watchers WERE
             being added on every click; nothing said so, and there was no way
             out of the control.
             
             The right fix was the handler, not the widget's mode. This file's
             own design note already said so: the picker holds no selection of
             its own, and the chip list below is the single account of who is
             watching. Single-select is that shape — one pick is one add, the
             menu closes, `value={null}` clears the input, and the new watcher
             appears in the list below where the state actually lives. */
          options={options}
          /* The casts here and on onChange are load-bearing, and they are
             documenting a lie rather than papering over one.
             
             AutocompleteWithLabel's own .d.ts declares its props as
             AutocompleteProps<Option, true, ...> — Multiple=TRUE
             unconditionally, regardless of the runtime `multiple` prop. So the
             declared types describe the multi-select shape even in single-select
             mode, where the runtime accepts a null value and hands back ONE
             option. That mismatch is what let the original bug type-check in
             both directions; bending this call site to satisfy the declaration
             would mean writing multi-select code again. Verified against the
             component source, not inferred. Remove these casts when the upstream
             typings branch on `multiple`. */
          value={null as unknown as UserOption[]}
          fullWidth
          disabled={busy}
          inputSize="small"
          label={getTranslate('Add watcher')}
          loading={loadingUsers || loadingEligible}
          loadingText={getTranslate('Loading') + '...'}
          getOptionLabel={(option) => option.label || ''}
          onChange={(_, selected) => {
            const option = selected as unknown as UserOption | null
            // Adding on selection rather than behind a second button: the
            // choice IS the action, and a confirm step would create a state
            // where the picker shows someone who is not yet watching.
            //
            // Null on clear, so the guard is required — MUI passes null when
            // the input is emptied rather than a value being chosen.
            if (option) add(option)
          }}
        />
      </div>
    </SectionCard>
  )
}
